import React, { useState } from 'react';
import { FaCheckCircle } from 'react-icons/fa';

interface PricingCardProps {
  title: string;
  price: number | string;
  description: string;
  features: string[];
  highlight?: boolean;
  badge?: string;
  billingType: string;
  isYearly: boolean;
}

const PricingCard: React.FC<PricingCardProps> = ({
  title,
  price,
  description,
  features,
  highlight = false,
  badge,
  billingType,
  isYearly,
}) => {
  const handleClick = () => {
    const billing = typeof price === 'number' ? `${billingType} - ₹${price}` : billingType;
    const message = `Hello, I'm interested in the ${title} plan (${billing}). Please provide more details.`;
    const phoneNumber = '918097588099';
    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  return (
    <div className={`w-full max-w-sm rounded-2xl shadow-md p-6 border relative transition-all duration-300 transform hover:scale-105 ${highlight ? 'border-[#6cbb23] scale-105' : 'border-gray-200'}`}> 
      {badge && (
        <span className="absolute top-4 right-4 bg-[#6cbb23] text-white text-xs font-semibold px-3 py-1 rounded-full">{badge}</span>
      )}
      <div>
        <h2 className="text-2xl font-semibold mb-2 text-[#6cbb23]">{title}</h2>
        <p className="text-3xl font-bold mb-1">
          ₹{price}{typeof price === 'number' ? `/${isYearly ? 'year' : 'month'}` : ''}
        </p>
        <p className="text-sm text-green-600 mb-1">3 months free trial</p>
        <p className="text-xs text-gray-500 italic mb-2">*Billed annually</p>
        <p className="mb-4 text-gray-600">{description}</p>
        <ul className="text-sm text-gray-700 mb-6 space-y-2">
          {features.map((feature, index) => (
            <li key={index} className="flex items-center gap-2">
              <FaCheckCircle className="text-[#6cbb23]" /> {feature}
            </li>
          ))}
        </ul>
        <button onClick={handleClick} className="w-full bg-[#6cbb23] hover:bg-[#5ca81f] text-white transition duration-300 py-2 px-4 rounded-lg">Choose Plan</button>
      </div>
    </div>
  );
};

export default function PricingSection() {
  const [isYearly, setIsYearly] = useState(false);

  const features = [
    'Digital Menu',
    'QR Codes',
    'QR Stands',
    'Order Placing',
    'Receive orders on WhatsApp',
    'Collect Takeaway Orders',
    'Collect Delivery Orders',  
    'Live Order Tracking for Kitchen',
    'No setup cost on Yearly Package'
  ];

  const pricingData = [
    {
      title: 'Small Business',
      monthly: 499,             // ₹349
      yearly: 499 * 9,        // ₹3141
      description: 'Up to 10 QR Codes'
    },
    {
      title: 'Medium Business',
      monthly: 999,
      yearly: 999 * 9,                // ₹6741
      description: 'Up to 20 QR Codes',
      highlight: true,
      badge: 'Most Popular'
    },
    {
      title: 'Large Business',
      monthly: 1499,
      yearly: 1499 * 9,                // ₹8991
      description: 'Up to 35 QR Codes'
    }
  ];

  return (
    <div className="min-h-screen py-10 px-4 bg-gray-100 md:px-16">
      <h1 className="text-4xl font-bold text-center text-black mb-6">Simple & Transparent  <strong className="text-[#6cbb23]">Pricing</strong> Plans</h1>

      <div className="flex justify-center mb-8 gap-4">
        <button
          className={`px-4 py-2 rounded-full font-medium ${!isYearly ? 'bg-[#6cbb23] text-white' : 'bg-gray-200 text-gray-700'}`}
          onClick={() => setIsYearly(false)}
        >
          Monthly Billing
        </button>
        <button
          className={`px-4 py-2 rounded-full font-medium ${isYearly ? 'bg-[#6cbb23] text-white' : 'bg-gray-200 text-gray-700'}`}
          onClick={() => setIsYearly(true)}
        >
          Yearly Billing (3 Months Free)
        </button>
      </div>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {pricingData.map((plan, index) => (
          <PricingCard
            key={index}
            title={plan.title}
            price={isYearly ? plan.yearly : plan.monthly}
            description={plan.description}
            features={features}
            highlight={plan.highlight}
            badge={plan.badge}
            billingType={isYearly ? 'Yearly Billing' : 'Monthly Billing'}
            isYearly={isYearly}
          />
        ))}
      </div>
    </div>
  );
}
